// eslint-disable-next-line import/named
import { addV6DebugInformation, clearAllV6DebugInformation } from './v6-debug-information.js';
import {
  applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections,
  assetContentTag,
  convertV6CommitDetails,
  fetchQuoteAsset,
  getQuoteFrameVersion,
  getV6QuoteItemForContainer,
  isSupplierUsingSSI,
  setProviderData
} from '../data/v6/helper-functions.js';
import {
  AttachedBlobAsset,
  InputUpdateQuoteItems,
  QuoteItemPrice,
  QuoteItemState,
  QuoteItemType
} from '../../api/dealer-api-interface-quote.js';
import {
  compareFrames,
  CompareReport,
  QuoteAttributeDefaultDataProvider,
  QuoteFrameDataProvider,
  v6FrameUserMode,
  V6PickerFamilyButton,
  V6QuoteFrameEditor,
  V6QuoteFrameEditorData
} from '../../../webmodule-common/interop/webmodule-interop.js';
import { createContentPreview } from '../../../webmodule-common/other/ui/templateresult/preview-helpers.js';
import { customElement } from 'lit/decorators.js';
import { DataBinding, getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding.js';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker.js';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid.js';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events.js';
import { firstValidString, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant.js';
import { FormInputAssistantBase } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant-base.js';
import { getQuoteItemTab, handleCommentEdit } from '../data/quote-helper-functions.js';
import { getQuoteSuppliers } from '../quote-service.js';
import { html, TemplateResult } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option.js';
import { nowServer } from '../../../webmodule-common/other/datetime-converter.js';
import { offline } from '../../../webmodule-common/other/ui/offline.js';
import { QuoteItemContainer } from '../data/quote-item-container.js';
import { QuoteItemView, QuoteItemViewOptions } from '../views/quote-item-view.js';
import { quoteSupplierProvider } from '../data/quoteSupplierProvider.js';
import { StockLookupView } from '../../api/dealer-api-interface-franchisee.js';
import { supplierQuoteItemContentType } from '../data/supplier-quote-item-content-type.js';

import { allItemIds, findGroup, getGroups, getItemGroupId } from '../data/quote-item-grouping.js';
import { glossary } from '../../../localization/global.js';
import { isDeletedQuoteItemState } from '../data/quote-container.js';
import { LangQuoteDetailView } from '../../../localization/scripts/quotes/views/quote-detail-view-lang';
import { LangV6QuoteItemFrameView } from '../../../localization/scripts/quotes/v6/v6-quote-item-frame-view-lang';
import { sleep } from '../../../webmodule-common/other/general/time.js';
import { toJsonStr } from '../../../webmodule-common/other/blob-converters.js';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import { userSecurity } from '../../../webmodule-common/other/api/user-security.js';
import { v6BootLoaderWithFailMsg } from '../../v6config/v6config.js';
import { v6Editors, v6Util } from '../../../webmodule-common/interop/interfaces/v6-config.js';
import type { FormInputSelectValue } from '../../../webmodule-common/other/ui/templateresult/form-input-types.js';

import { waitPatientlySavingWithLock } from '../../../webmodule-common/other/ui/modal-saving.js';

enum V6QuoteItemSaveState {
  nothing = 0,
  prepareForSave = 10,
  communicateWithSupplierSystem = 20,
  getBuyInCosts = 25,
  updatePricing = 28,
  CollectQuoteContract = 40,
  RequestSSI = 50,
  UpdateLocalSSI = 60,
  UpdateAllQuoteItems = 70,
  UpdateComplete = 80
}

@customElement('wm-quoteitemframeviewforv6')
export class QuoteItemFrameViewForV6 extends QuoteItemView implements QuoteFrameDataProvider {
  v6Editor: V6QuoteFrameEditor | null = null;
  dataTracker: DataTracker;
  framePicker?: V6PickerFamilyButton;
  private supplierOffline = false;
  /**
   * Pending group assignment chosen in the properties panel, applied as part of the editor's save
   * flow (not immediately). `undefined` means the user has not touched the selector; otherwise it
   * holds the chosen target group id, or `null` for the trunk (ungrouped).
   */
  private _pendingGroupId: string | null | undefined = undefined;
  /**
   * When set, the user typed a brand-new group name in the combobox ("Create ..."). On save the
   * group is created and the item moved into it. Takes precedence over `_pendingGroupId`.
   */
  private _pendingNewGroupName: string | null = null;
  diffReport: {
    report: CompareReport | null;
    markdown: string;
  } | null;
  commitAssets: { [key: string]: AttachedBlobAsset };

  constructor(options: QuoteItemViewOptions) {
    super(options);

    this.dataTracker = new DataTracker(new DataBinding(this.ui, getInternalId()));
    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.quoteItemContainer?.item),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('title');
    addField('description');

    this.dataTracker.addDynamic(
      'comment',
      FieldType.string,
      () => {
        return this.quoteItemContainer?.item?.comment;
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      { nullable: true }
    );
    this.dataTracker.addDynamic(
      'commentToManufacturer',
      FieldType.string,
      () => {
        return this.quoteItemContainer?.item?.commentToManufacturer;
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      { nullable: true }
    );

    // Changed nullable to true since we are checking the field in the validation.
    addField('quantity', FieldType.int, true);
    addField('lastModifiedDate', FieldType.dateTime, true);
  }

  //only used for new frames
  private _id: string = newGuid();

  get id(): string {
    return this.quoteItemContainer?.item.id ?? this._id;
  }

  get quoteAttributeDefaultDataProvider(): QuoteAttributeDefaultDataProvider | undefined {
    return this;
  }
  getBaseLineQuoteDefaults(): Promise<unknown | undefined> {
    return Promise.resolve(undefined);
  }
  get supplierReferenceOverrideKey(): string {
    //the quote owner is the branch ID, which is what we are tying our v6 customer to
    return this.quoteManager.quote.quoteOwnerId;
  }

  get readonly(): boolean {
    return this.isDataReadonly() || this.supplierOffline;
  }

  get quoteItem() {
    if (!this.quoteItemContainer) return undefined;
    return getV6QuoteItemForContainer(this.quoteItemContainer);
  }

  get quoteItemVersion(): string | undefined {
    if (!this.quoteItemContainer) return undefined;
    return getQuoteFrameVersion(this.quoteItemContainer);
  }

  get quoteIGUs() {
    return this.getQuoteIGUs();
  }

  get userMode(): string | undefined {
    if (userSecurity().isPowerUser()) return v6FrameUserMode.powerUser;
    else return v6FrameUserMode.standardUser;
  }

  get quantity(): number {
    if (!this.quoteItemContainer) return 1;
    try {
      const uiValue = Math.max(1, this.getQuantityFromUI());
      return uiValue;
    } catch {
      return this.quoteItemContainer?.item.quantity ?? 1;
    }
  }

  get thumbnailURL() {
    const quoteItem = this.quoteItemContainer?.item;
    return this.quoteApi.convertQuoteItemAssetPathToUrl(
      quoteItem?.quoteId,
      quoteItem?.id,
      quoteItem?.virtualThumbnailPath,
      // Without a tag this URL never changes, so re-rendering after a frame edit leaves the old
      // image on screen - the browser is never asked for it again. See assetContentTag.
      assetContentTag(this.quoteItemContainer?.data?.assetVirtualPaths, 'thumbnail', quoteItem?.recordVersion)
    );
  }

  get quoteDefaults() {
    return this.getQuoteDefaults();
  }

  //QuoteFrameDataProvider
  async dispose(): Promise<void> {
    await super.dispose();
    if (!this.readonly) this.v6Editor?.releaseResources();
    this.v6Editor = null;
    await clearAllV6DebugInformation('QuoteItemFrameViewForV6');
  }

  /**
   * inherited
   * @returns
   */
  public isTab(): boolean {
    return true;
  }

  /**
   * inherited
   * @returns
   */
  public async prepareForSave(): Promise<void> {
    await this.setQuoteItemSaveState(V6QuoteItemSaveState.prepareForSave);
    if (this.readonly) return;
    if (!this.v6Editor) return;
    if (this.quoteItemContainer) {
      if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();

      //the provider data is a base64 encoding of the v6configuration data
      //normally we only want to assign this data when we hit the save button
      //for now, we are updating the quoteitem every single call back so that
      //it stress tests the system
      //TODO-we dont need to adjust this every single time normally, only when
      if (!this.quoteItemContainer.data)
        throw new DevelopmentError('v6-quote-item-frame-view, quoteItemContainer.data is null');

      //design changes now mean that we always need to trigger the server call to update buyin items, pricing, and server side macro
      //events. we can't assume nothing has changed.
      await this.setQuoteItemSaveState(V6QuoteItemSaveState.communicateWithSupplierSystem);
      let details: V6QuoteFrameEditorData | null = null;
      try {
        if (this.v6Editor.changed || this.quoteManager.changedItemQuantity(this.quoteItemContainer?.item.id))
          details = await this.v6Editor.commit();
      } catch {
        await offline();
      }
      if (details) {
        const buyIn = details?.buyInItems;
        const price = details.price;
        this.commitAssets = convertV6CommitDetails(details);

        const originalFrameData = this.quoteItem ? v6Util().getFrameData(this.quoteItem as any) : null;
        const newFrameData = details.quoteItem ? v6Util().getFrameData(details.quoteItem as any) : null;
        const frameDiff = compareFrames(originalFrameData, newFrameData, { excludeHidden: true });
        this.quoteItemContainer.differenceReport = frameDiff;
        if (buyIn && buyIn.length > 0) await this.setQuoteItemSaveState(V6QuoteItemSaveState.getBuyInCosts);
        const buyInCosts = await this.quoteManager.getBuyInCosts(buyIn);
        const buyInMetaData = this.buyInDataJsonStr(buyInCosts);

        this.quoteItemContainer.item.isRestrictedToPowerUser = details.requiredUserMode === v6FrameUserMode.powerUser;
        await this.setQuoteItemSaveState(V6QuoteItemSaveState.updatePricing);

        applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
          this.quoteItemContainer.item.quantity,
          price,
          this.quoteItemContainer.price,
          buyInCosts
        );

        this.quoteItemContainer.buyInData = buyInMetaData ?? undefined;
        // Set providerData last - this also flushes the clientSideCache
        setProviderData(this.quoteItemContainer, this.commitAssets['data']?.data ?? null);
      }
    }
  }
  public async fetchAsset(assetType: string): Promise<string | object | null> {
    if (!this.quoteItemContainer) return null;
    const newPath = this.quoteItemContainer.data?.assetVirtualPaths?.[assetType]?.path ?? '';
    const publicUrl = this.quoteItemContainer.data?.assetVirtualPaths?.[assetType]?.publicUrl ?? null;
    const v6Data = getV6QuoteItemForContainer(this.quoteItemContainer) as any;

    switch (assetType) {
      case 'model3d':
        return (
          v6Data.vrml ??
          (await fetchQuoteAsset({
            quoteId: this.quoteItemContainer.item.quoteId,
            quoteItemId: this.quoteItemContainer.item.id,
            assetPath: newPath,
            publicUrl: publicUrl
          }))
        ); // Handle 3D model asset
        break;
      case 'bomView':
        v6Data.bomView ??
          (await fetchQuoteAsset({
            quoteId: this.quoteItemContainer.item.quoteId,
            quoteItemId: this.quoteItemContainer.item.id,
            assetPath: newPath,
            publicUrl: publicUrl
          }));
        break;
      case 'thumbnail':
        return null; //notsupported
        break;
      case 'data':
        return v6Data;
        break;
      default:
        return null;
    }
    return null;
  }

  public override async afterConstruction(): Promise<void> {
    await super.afterConstruction();
    // preload the grouping document so the properties panel can offer group assignment. Grouping is
    // optional (not every quote/context has groups), so a load failure must not block the editor.
    try {
      await this.quoteManager.needsItemGrouping();
    } catch {
      // ignore - the group selector simply will not render
    }
  }

  public async prepareEditor(): Promise<void> {
    try {
      if (await v6BootLoaderWithFailMsg()) {
        if (!this.readonly) {
          const suppliers = await getQuoteSuppliers();
          this.supplierOffline = !(
            suppliers.find(x => x.supplierId === this.quoteManager.quote.supplierId)?.online ?? false
          );
          if (this.supplierOffline) await information(LangV6QuoteItemFrameView.supplierOfflineReadonly());
        }
        await addV6DebugInformation('QuoteItemFrameViewForV6', {
          quoteItemId: this._id,
          quoteItemPosition: this.quoteManager.itemPosition(this._id, true)?.toString() ?? '',
          quoteItemTitle: 'New Item'
        });
        this.v6Editor = await v6Editors().getQuoteFrameEditorService(this);
        if (this.v6Editor) {
          this.v6Editor.ui.addEventListener('v6frameview', e => {
            const ev: CustomEvent = e as CustomEvent;
            if (ev.detail.error && ev.detail.shutdownRequired) this.abandonAndClose(true);
          });
        }
      }
      this._readyToEdit = this.v6Editor != null;
      if (this.v6Editor && !this.quoteItemContainer) {
        this._readyToEdit = false;
        let details: V6QuoteFrameEditorData | null = null;
        try {
          details = await this.v6Editor.commit();
        } catch {
          await offline();
          return;
        }
        if (details) {
          const price = details.price;
          const assets = convertV6CommitDetails(details);

          const providerType = quoteSupplierProvider.v6;
          const providerReferenceId: string = emptyGuid;

          const buyIn = details.buyInItems;
          const buyInCosts = await this.quoteManager.getBuyInCosts(buyIn);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { quoteItemCommonId, currency, ...priceSourceData } = price;
          const id = this._id;
          const date = nowServer();

          //create mock price object so we can use the same
          //code for generating the price
          const qprice: QuoteItemPrice = {
            breakdowns: null,
            calculatedGrossSellingPrice: 0,
            calculatedNetSellingPrice: 0,
            dateCreated: date,
            id: id,
            percentMarginOrMarkup: 0,
            priceAdjustment: 0,
            quantityCost: 0,
            recordVersion: '',
            requiresRepricing: false,
            singleUnitCost: 0,
            sourceData: priceSourceData,
            supplierGrossQuantityCost: 0,
            supplierGrossSingleUnitCost: 0,
            supplierNettQuantityCost: 0,
            supplierNettSingleUnitCost: 0,
            supplierPriceAdjustment: 0,
            isTaxableItem: true,
            supplierPriceAdjustmentEnabled: false,
            supplierPriceAdjustmentReason: null
          };
          applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(1, price, qprice, buyInCosts);

          const buyInMetaData = this.buyInDataJsonStr(buyInCosts);
          //we need to populate the dataprovider correctly because SSI processing uses the real embedded price information.
          const exInput: QuoteItemContainer = {
            data: {
              assetVirtualPaths: {},
              dateCreated: date,
              lastModifiedDate: date,
              lastModifiedUserId: emptyGuid,
              recordVersion: '',
              id: id,
              providerData: assets['data']?.data ?? null
            },

            buyInData: buyInMetaData ?? undefined,
            item: {
              comment: null,
              commentToManufacturer: null,
              commonId: id,
              dateCreated: date,
              description: this.v6Editor.frameDescription,
              id: id,
              isRestrictedToPowerUser: details.requiredUserMode === v6FrameUserMode.powerUser,
              itemType: QuoteItemType.Provider,
              lastModifiedDate: date,
              lastModifiedUserId: emptyGuid,
              providerReferenceId: providerReferenceId,
              serviceProvider: providerType,
              quantity: 1,
              quoteId: this.quoteManager.quoteId,
              quoteItemContentType: supplierQuoteItemContentType.CID_FRAM,
              recordVersion: '',
              title: LangV6QuoteItemFrameView.frameTitle(),
              virtualThumbnailPath: '',
              isShippingItem: false,

              state: QuoteItemState.Active
            },
            price: qprice
          };

          const ssiProcessor = this.quoteManager.ssiProcessor(undefined, [exInput]);
          const ssiInputs = await ssiProcessor.processSSI();

          this.quoteItemContainer = await this.quoteManager.createQuoteItemEx(exInput, assets, ssiInputs);
        }
        this._readyToEdit = this.quoteItemContainer != null;
      }
      if (this._readyToEdit && this.quoteItemContainer) {
        await addV6DebugInformation('QuoteItemFrameViewForV6', {
          quoteItemId: this.quoteItemContainer.item.id,
          quoteItemPosition: this.quoteManager.itemPosition(this.quoteItemContainer.item.id)?.toString() ?? '',
          quoteTitle: this.quoteItemContainer.item.title,
          quoteItemDescription: this.quoteItemContainer.item.description
        });
      }
    } catch (e) {
      await showDevelopmentError(e as Error);
      this._readyToEdit = false;
    }
  }

  private createCommentButton(fieldName: string, forms: FormInputAssistant): { label: string; readonly: boolean } {
    const currentValue = this.dataTracker.getObjectValue(fieldName) as string;

    if (forms.forceReadonly) {
      return {
        label: LangV6QuoteItemFrameView.viewNote(),
        readonly: !currentValue || isEmptyOrSpace(currentValue)
      };
    }

    return {
      label: isEmptyOrSpace(currentValue) ? LangV6QuoteItemFrameView.addNote() : LangV6QuoteItemFrameView.editNote(),
      readonly: false
    };
  }

  private async handleCommentEdit(
    fieldName: 'comment' | 'commentToManufacturer',
    title: string,
    forms: FormInputAssistant
  ): Promise<void> {
    await handleCommentEdit(fieldName, title, this.quoteItemContainer, forms.forceReadonly, () => {
      this.v6Editor?.requestUpdate();
      this.requestUpdate();
    });
  }

  /**
   * Dropdown that assigns this item to one of the quote's item groups (a dealer-side concept, not a
   * V6 property). Selecting a group moves the item into it; "Ungrouped" removes it from its group.
   * Only shown when the quote actually has groups, the item is groupable, and it is already part of
   * the grouping document (a brand-new, unsaved item is not yet known to the document).
   */
  private groupSelectorTemplate(forms: FormInputAssistantBase): TemplateResult {
    const item = this.quoteItemContainer?.item;
    if (!item || this.quoteManager.itemIsNotSortable(item) || isDeletedQuoteItemState(item)) return html``;

    const doc = this.quoteManager.quoteItemGrouping;
    if (!doc) return html``;
    // the item must be part of the grouping document (saved + groupable). Groups may be empty - the
    // combobox's allow-create lets the user create the first group from here.
    const groups = getGroups(doc);
    if (!allItemIds(doc).has(item.commonId)) return html``;

    // the value the combobox should show: a pending new-group name, then a pending existing/trunk
    // choice, otherwise the persisted group
    const selectedValue =
      this._pendingNewGroupName ??
      (this._pendingGroupId !== undefined ? (this._pendingGroupId ?? '') : (getItemGroupId(doc, item.commonId) ?? ''));

    // no explicit "ungrouped" option: an empty value shows the placeholder, and the combobox's
    // clear button moves the item back to the trunk
    const values: FormInputSelectValue[] = groups.map(group => ({
      value: group.id,
      text: group.name,
      disabled: false
    }));

    return forms.combobox({
      label: LangV6QuoteItemFrameView.groupAssignment(),
      value: selectedValue,
      values,
      allowCreate: true,
      clearable: true,
      placeholder: LangV6QuoteItemFrameView.groupPlaceholder(),
      emptyText: LangV6QuoteItemFrameView.noGroupsYet(),
      getCreateLabel: (name: string) => LangV6QuoteItemFrameView.createGroupOption({ name }),
      readonly: this.quoteManager.isReadonly(),
      events: {
        change: (e: Event) => {
          const value = ((e.target as HTMLElement & { value?: string }).value ?? '').toString();
          this.selectPendingGroup(value);
        },
        create: (e: CustomEvent<{ value: string }>) => this.selectPendingNewGroup(e.detail.value)
      }
    });
  }

  /** the item's persisted group id (null when in the trunk / not grouped). */
  private persistedGroupId(): string | null {
    const item = this.quoteItemContainer?.item;
    const doc = this.quoteManager.quoteItemGrouping;
    if (!item || !doc) return null;
    return getItemGroupId(doc, item.commonId);
  }

  /** true when a deferred group assignment (existing, trunk, or new) differs from the persisted group. */
  private hasPendingGroupChange(): boolean {
    if (this._pendingNewGroupName !== null) return true;
    return this._pendingGroupId !== undefined && this._pendingGroupId !== this.persistedGroupId();
  }

  /** record the chosen existing group (empty value => trunk) without persisting; applied on save. */
  private selectPendingGroup(rawValue: string): void {
    // ignore the change that fires right after creating a new group (value === the new name)
    if (this._pendingNewGroupName !== null && rawValue === this._pendingNewGroupName) return;
    this._pendingNewGroupName = null;
    this._pendingGroupId = isEmptyOrSpace(rawValue) ? null : rawValue;
    // re-render so the dirty/save state reflects the pending change
    this.refreshParent();
  }

  /** record a brand-new group name to create on save. */
  private selectPendingNewGroup(name: string): void {
    this._pendingNewGroupName = isEmptyOrSpace(name) ? null : name.trim();
    this._pendingGroupId = undefined;
    this.refreshParent();
  }

  /**
   * Apply the deferred group assignment. Called from the save flow AFTER the item itself has been
   * saved, so the move runs against the freshly persisted item (correct record version) instead of
   * mid-edit. The move persists itself (grouping save + any exclude-from-pricing reprice).
   */
  private async applyPendingGroupAssignment(): Promise<void> {
    const item = this.quoteItemContainer?.item;
    const doc = this.quoteManager.quoteItemGrouping;
    if (!item || !doc) return;

    // resolve the target group: create a new one first if the user typed a new name
    let groupId: string | null;
    if (this._pendingNewGroupName !== null) {
      const created = await this.quoteManager.createItemGroup(this._pendingNewGroupName);
      if (!created) return;
      // the group is now persisted; demote the pending state to an existing-group move so that if
      // the move below fails, a retry moves into the created group instead of creating a duplicate
      this._pendingNewGroupName = null;
      this._pendingGroupId = created;
      groupId = created;
    } else if (this._pendingGroupId !== undefined) {
      groupId = this._pendingGroupId;
    } else {
      return;
    }

    // append to the end of the target group (or the trunk when ungrouping). Re-read the doc since
    // createItemGroup may have rebuilt it.
    const currentDoc = this.quoteManager.quoteItemGrouping ?? doc;
    const targetEnd =
      groupId === null ? currentDoc.root.length : (findGroup(currentDoc, groupId)?.children.length ?? 0);
    const moved = await this.quoteManager.moveItemToGroup(item.commonId, groupId, targetEnd);
    if (moved) {
      this._pendingGroupId = undefined;
      this._pendingNewGroupName = null;
      // the move may have bumped the item's record version; re-sync the held container
      this.quoteItemContainer = this.quoteManager.quoteItemContainer(item.id);
    }
  }

  propertiesTemplate(): TemplateResult {
    const forms = new FormInputAssistant(this.dataTracker, this.quoteManager.isReadonly());
    const formsNonForced = new FormInputAssistantBase(false);

    if (!this.quoteItemContainer?.item) {
      return html``;
    }

    const customerNoteButton = this.createCommentButton('comment', forms);
    const manufacturerNoteButton = this.createCommentButton('commentToManufacturer', forms);

    const handleCustomerNote = () => this.handleCommentEdit('comment', LangV6QuoteItemFrameView.notesToClient(), forms);

    const handleManufacturerNote = () =>
      this.handleCommentEdit('commentToManufacturer', LangV6QuoteItemFrameView.notesToManufacturer(), forms);

    // Build button for client notes
    const clientNoteButtonElement = formsNonForced.buttonPrimary(customerNoteButton.label, {
      events: { click: handleCustomerNote },
      readonly: customerNoteButton.readonly
    });
    // Conditionally wrap in preview popup
    const clientNoteSlotted = this.quoteItemContainer?.item.comment
      ? createContentPreview(
          clientNoteButtonElement,
          LangV6QuoteItemFrameView.notesToClient(),
          this.quoteItemContainer.item.comment
        )
      : clientNoteButtonElement;

    // Build button for manufacturer notes
    const manufacturerNoteButtonElement = formsNonForced.buttonPrimary(manufacturerNoteButton.label, {
      events: { click: handleManufacturerNote },
      readonly: manufacturerNoteButton.readonly
    });
    // Conditionally wrap in preview popup
    const manufacturerNoteSlotted = this.quoteItemContainer?.item.commentToManufacturer
      ? createContentPreview(
          manufacturerNoteButtonElement,
          LangV6QuoteItemFrameView.notesToManufacturer(),
          this.quoteItemContainer.item.commentToManufacturer
        )
      : manufacturerNoteButtonElement;

    return html`
      <div class="form-one-col quoteItemFrameProperties">
        <div class="row attributeContents">
          <div class="v6config-group">
            <div class="v6config-group-header">${LangV6QuoteItemFrameView.propertiesSectionTitle()}</div>
            ${forms.text('title', LangV6QuoteItemFrameView.frameTitle(), 120)}
            ${forms.text('description', LangV6QuoteItemFrameView.description(), 120)}
            ${forms.int('quantity', LangV6QuoteItemFrameView.quantity())} ${this.groupSelectorTemplate(formsNonForced)}
            ${formsNonForced.multiEditRow({
              title: LangV6QuoteItemFrameView.notesToClient(),
              toolTip: LangV6QuoteItemFrameView.notesToClientTooltip(),
              slotted: clientNoteSlotted
            })}
            ${formsNonForced.multiEditRow({
              title: LangV6QuoteItemFrameView.notesToManufacturer(),
              toolTip: LangV6QuoteItemFrameView.notesToManufacturerTooltip(),
              slotted: manufacturerNoteSlotted
            })}
            ${userDataStore.isFabricatorMode
              ? forms.dateReadonly('lastModifiedDate', LangQuoteDetailView.lastModified())
              : html``}
          </div>
        </div>
      </div>
    `;
  }

  public internalDataChanged(): boolean {
    return (
      !this.readonly &&
      (this.quoteManager.changedItem(this.quoteItemContainer?.item.id) ||
        this.quoteManager.quoteChanged() ||
        this.hasPendingGroupChange() ||
        (this.v6Editor?.changed ?? false))
    );
  }

  displaySaveModal(): boolean {
    return true;
  }

  public allowDeletePage(): boolean {
    return true;
  }

  discardLabel(): unknown {
    return LangV6QuoteItemFrameView.discardFrameChanges();
  }

  buttonMenu(): Snippet {
    return html`${this.abandonAndCloseButton()}`;
  }

  getDataDictionaryName(): string {
    return glossary('frame');
  }

  public isDataReadonly(): boolean {
    if (!this.quoteItemContainer) return true;
    return this.quoteManager.isReadonly() || isDeletedQuoteItemState(this.quoteItemContainer.item);
  }

  protected revertChanges(): Promise<boolean> {
    // drop any deferred group assignment
    this._pendingGroupId = undefined;
    this._pendingNewGroupName = null;
    if (this.quoteItemContainer) {
      const backup = this.quoteManager.quoteItemContainer(this.quoteItemContainer.item.id, true);
      this.quoteItemContainer.item.title = backup.item.title;
      this.quoteItemContainer.item.description = backup.item.description;
      this.quoteItemContainer.item.quantity = backup.item.quantity;
      this.quoteItemContainer.item.comment = backup.item.comment;
    }
    return Promise.resolve(true);
  }

  protected getValidationErrors(): string[] {
    const errors: string[] = [];
    const quantity = this.getQuantityFromUI();
    if (isNaN(quantity) || quantity < 1) errors.push(LangV6QuoteItemFrameView.invalidQuantity({ quantity }));

    return errors;
  }

  protected async internalSaveData(): Promise<boolean> {
    //TODO- At this point, we have done everything needed to save the frame item. now we need to update the entire quote.
    let updateItems: InputUpdateQuoteItems | undefined = undefined;
    if (isSupplierUsingSSI(this.quoteManager.quote.supplierId)) {
      await this.setQuoteItemSaveState(V6QuoteItemSaveState.RequestSSI);
      const ssi = this.quoteManager.ssiProcessor();
      updateItems = await ssi.processSSI();

      await this.setQuoteItemSaveState(V6QuoteItemSaveState.UpdateLocalSSI);
    }

    await this.setQuoteItemSaveState(V6QuoteItemSaveState.UpdateAllQuoteItems);
    try {
      if (this.quoteItemContainer) {
        this.quoteItemContainer = await this.quoteManager.saveAndUpdateQuoteItem(
          this.quoteItemContainer,
          this.commitAssets,
          updateItems
        );
        const saved = this.quoteManager.lastSaveSuccessful;
        // apply a deferred group assignment only once the item is safely persisted, so the move
        // runs against the current record version rather than mid-edit
        if (saved && this.hasPendingGroupChange()) await this.applyPendingGroupAssignment();
        return saved;
      }

      return false;
    } finally {
      await this.setQuoteItemSaveState(V6QuoteItemSaveState.UpdateComplete);
    }
  }

  protected getCaption(): Snippet {
    return getQuoteItemTab(
      firstValidString(
        this.quoteItemContainer?.item.title,
        this.v6Editor?.frameDescription,

        LangV6QuoteItemFrameView.newFrame()
      ),
      this.quoteManager.itemPosition(this.quoteItemContainer?.item.id)
    );
  }

  protected bodyTemplate(): EventTemplate {
    if (this.v6Editor) {
      return html`${this.v6Editor.ui}`;
    } else return html``;
  }

  protected getQuoteIGUs(): unknown {
    throw new DevelopmentError('getQuoteIGUs() must override');
  }

  saveQuoteDefaults(_quoteDefaultsData: unknown): boolean {
    throw new DevelopmentError('saveQuoteDefaults() must override');
  }

  saveAttributeUpdatedHistory(_quoteAttributeHistoryData: unknown): boolean {
    throw new DevelopmentError('saveAttributeUpdatedHistory() must override');
  }

  getAttributeUpdatedHistory(): unknown {
    throw new DevelopmentError('getAttributeUpdatedHistory() must override');
  }

  getQuoteDefaults(): unknown {
    throw new DevelopmentError('getQuoteDefaults() must override');
  }

  getGlobalAttributesMasterSet(): unknown {
    throw new DevelopmentError('getGlobalAttributesMasterSet() must override');
  }

  saveGlobalAttributesMasterSet(_data: unknown): boolean {
    throw new DevelopmentError('getGlobalAttributesMasterSet() must override');
  }
  private async v6BusyCheck() {
    if (this.v6Editor) {
      const awaiter = (this.v6Editor as any).updateComplete as Promise<void> | null | undefined;
      if (awaiter !== undefined) await awaiter;
      else {
        await sleep(20);
        while (this.v6Editor?.busy) {
          await sleep(20);
        }
      }
    }
  }

  protected async readyToClose(): Promise<boolean> {
    await waitPatientlySavingWithLock({ action: this.v6BusyCheck() });
    return true;
  }

  private async setQuoteItemSaveState(state: V6QuoteItemSaveState) {
    let info = '';
    const qic = this.quoteItemContainer;
    const qi = qic?.item;
    if (!qi) {
      await this.informationDispatcher.setInformation('');
      return;
    }
    switch (state) {
      case V6QuoteItemSaveState.nothing:
        info = LangV6QuoteItemFrameView.editingFrame({ title: qi.title });
        break;
      case V6QuoteItemSaveState.prepareForSave:
        info = LangV6QuoteItemFrameView.preparingToSave({ title: qi.title });
        break;
      case V6QuoteItemSaveState.communicateWithSupplierSystem:
        info = LangV6QuoteItemFrameView.preparingToSaveWithSupplier({ title: qi.title });
        break;
      case V6QuoteItemSaveState.CollectQuoteContract:
        info = LangV6QuoteItemFrameView.prepareInformationForSupplier({ title: qi.title });
        break;
      case V6QuoteItemSaveState.updatePricing:
        info = LangV6QuoteItemFrameView.updatingQuotePrice({ title: qi.title });
        break;
      case V6QuoteItemSaveState.RequestSSI:
        info = LangV6QuoteItemFrameView.requestingUpdatesFromSupplier({ title: qi.title });
        break;
      case V6QuoteItemSaveState.UpdateLocalSSI:
        info = LangV6QuoteItemFrameView.updatingQuoteWithSupplierChanges({ title: qi.title });
        break;
      case V6QuoteItemSaveState.UpdateAllQuoteItems:
        info = LangV6QuoteItemFrameView.savingSendingUpdates({ title: qi.title });
        break;
      case V6QuoteItemSaveState.UpdateComplete:
        info = LangV6QuoteItemFrameView.savingCompleted({ title: qi.title });
        break;
    }
    await this.informationDispatcher.setInformation(info);
  }

  private getQuantityFromUI(): number {
    return this.dataTracker.getEditorValue('quantity') as number;
  }

  private buyInDataJsonStr(buyInCosts?: StockLookupView[]): string | undefined {
    if (buyInCosts && buyInCosts.length > 0) {
      return toJsonStr(buyInCosts) ?? undefined;
    } else return undefined;
  }
}
